// src/pages/PublicCompetitionRegistrations.tsx
//
// Public, read-only view of every athlete registered for a single competition,
// grouped by Club → Category (age group) → Weight class. Linked from
// PublicEvents.tsx.
//
// Privacy: only athleteName + club + division + weightCategory + category +
// coach name + weight-change history are shown. dateOfBirth, coachId,
// coachEmail, referenceCode, paymentId, price and paymentType are NOT rendered.
//
// Live-ness: this page reads through the existing 2-min `withCache` layer.
// A "Refresh" button calls invalidateCachePrefix and re-fetches. For true
// real-time updates we'd switch to onSnapshot, but the cache + manual
// refresh is enough for v1 traffic.

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  getWeightChangeDeadline,
} from "../firebase/registrations";
import {
  listenPublicRegistrations,
  PublicRegistrationSummary,
} from "../firebase/publicRegistrations";
import { getAllCompetitions } from "../firebase/competitions";
import { getCompetitionStatus } from "../utils/competitionStatus";
import { invalidateCachePrefix } from "../utils/cache";
import { formatDate } from "../utils/dateHelpers";
import { useTranslation } from "../hooks/useTranslation";
import PublicHeader from "../components/PublicHeader";
import { LockIcon, RefreshIcon, HistoryIcon, UsersIcon, ClockIcon } from "../components/Icons";
import { C, FONT } from "../theme";

// Sentinel for registrations with no club; rendered via t("publicRegs.noClub").
const NO_CLUB = "__no_club__";

// Desktop table columns for one athlete row: Athlete | Club | Coach | Last update.
const ROW_COLS = "minmax(0, 1.6fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1.4fr)";

// Category sort order — mirrors the canonical age-group sequence used in
// ManageCompetitions.tsx (Kids → Seniors). Unknown categories sort last.
const CATEGORY_ORDER: Record<string, number> = {
  "Kids A": 0,
  "Kids B": 1,
  "Cadets": 2,
  "Juniors": 3,
  "U21": 4,
  "Seniors": 5,
};

function categoryRank(c: string): number {
  return CATEGORY_ORDER[c] ?? 999;
}

// Weight sort — numeric ascending, with "+xx" classes at the end. Mirrors
// the sortWeights helper inside ManageCompetitions.tsx.
function weightRank(w: string): number {
  const isPlus = w.startsWith("+");
  const n = parseInt(w.replace(/[^0-9]/g, ""), 10) || 0;
  return isPlus ? 10_000 + n : n;
}

// DD/MM/YYYY for the "weight updated on …" line.
function formatDMY(d: Date | null): string {
  if (!d) return "";
  return d.toLocaleDateString("en-GB");
}

function toDateMaybe(ts: any): Date | null {
  if (!ts) return null;
  const d = typeof ts.toDate === "function" ? ts.toDate() : new Date(ts);
  return isNaN(d.getTime()) ? null : d;
}

// ── Responsive hook ─────────────────────────────────────────────────────────
function useWindowWidth(): number {
  const [w, setW] = useState(typeof window !== "undefined" ? window.innerWidth : 1024);
  useEffect(() => {
    const h = () => setW(window.innerWidth);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  return w;
}

// Coach display for the PUBLIC page. Prefers the denormalized `coachName`
// snapshot; falls back to a friendly name derived from the (already public)
// coachEmail local-part so older / server-written online docs still show who
// registered the athlete. Never renders the raw email itself.
function coachDisplay(coachName?: string, coachEmail?: string): string {
  if (coachName && coachName.trim()) return coachName.trim();
  if (coachEmail && coachEmail.includes("@")) {
    const local = coachEmail.split("@")[0];
    const pretty = local
      .split(/[._-]+/)
      .filter(Boolean)
      .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
      .join(" ");
    if (pretty) return pretty;
  }
  return "—";
}

// `weightChangedBy` is a stored display name, but may be an email on older
// rows — prettify it the same way so we never surface a raw address.
function changedByDisplay(by: string): string {
  if (!by) return "";
  return by.includes("@") ? coachDisplay(undefined, by) : by;
}

interface PublicAthlete {
  firstName: string;
  lastName: string;
  club: string;
  division: "Male" | "Female";
  weightCategory: string;
  category: string;
  coach: string;
  weightLastChanged: Date | null;
  weightChangedBy: string;
}

// A weight bracket is one (division + weight class) within a category — the
// real "who fights whom" unit. Athletes from every club share a bracket.
interface BracketGroup {
  key: string;
  division: "Male" | "Female";
  weightCategory: string;
  athletes: PublicAthlete[];
}

interface CategoryGroup {
  category: string;
  totalAthletes: number;
  brackets: BracketGroup[];
}

// Male before Female within a category; unknown divisions sort last.
const DIVISION_RANK: Record<string, number> = { Male: 0, Female: 1 };

function toPublicAthlete(r: PublicRegistrationSummary): PublicAthlete {
  return {
    firstName: r.athleteSnapshot?.firstName ?? "",
    lastName: r.athleteSnapshot?.lastName ?? "",
    club: r.club ?? "",
    division: r.division,
    weightCategory: r.weightCategory ?? "",
    category: r.category ?? "—",
    coach: coachDisplay(r.coachName),
    weightLastChanged: toDateMaybe((r as any).weightLastChanged),
    weightChangedBy: (r as any).weightChangedBy ?? "",
  };
}

// Group: Category (age group) → weight bracket (division + weight class) →
// athletes[]. Club is deliberately NOT a grouping level — every athlete in a
// bracket is listed together regardless of club, but sorted by club so
// clubmates land next to each other.
function groupByCategory(regs: PublicRegistrationSummary[]): CategoryGroup[] {
  const cats = new Map<string, Map<string, PublicAthlete[]>>();

  for (const r of regs) {
    const a = toPublicAthlete(r);
    if (a.division !== "Male" && a.division !== "Female") continue;
    if (!cats.has(a.category)) cats.set(a.category, new Map());
    const bMap = cats.get(a.category)!;
    const bKey = `${a.division}__${a.weightCategory}`;
    if (!bMap.has(bKey)) bMap.set(bKey, []);
    bMap.get(bKey)!.push(a);
  }

  const out: CategoryGroup[] = Array.from(cats.entries()).map(([category, bMap]) => {
    const brackets: BracketGroup[] = Array.from(bMap.entries())
      .map(([key, athletes]) => {
        const sep = key.indexOf("__");
        const division = key.slice(0, sep) as "Male" | "Female";
        const weightCategory = key.slice(sep + 2);
        return {
          key,
          division,
          weightCategory,
          // Clubmates adjacent: sort by club first, then by athlete name.
          athletes: athletes.sort((x, y) => {
            const c = x.club.localeCompare(y.club);
            if (c !== 0) return c;
            return `${x.lastName} ${x.firstName}`.localeCompare(`${y.lastName} ${y.firstName}`);
          }),
        };
      })
      .sort((a, b) => {
        const d = (DIVISION_RANK[a.division] ?? 9) - (DIVISION_RANK[b.division] ?? 9);
        if (d !== 0) return d;
        return weightRank(a.weightCategory) - weightRank(b.weightCategory);
      });
    const total = brackets.reduce((s, b) => s + b.athletes.length, 0);
    return { category, totalAthletes: total, brackets };
  });

  return out.sort((a, b) => categoryRank(a.category) - categoryRank(b.category));
}

export default function PublicCompetitionRegistrations() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, language } = useTranslation();
  const winW = useWindowWidth();
  const isMobile = winW < 768;

  const [comp, setComp] = useState<any | null>(null);
  const [regs, setRegs] = useState<PublicRegistrationSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [lastLoaded, setLastLoaded] = useState<Date | null>(null);
  const [search, setSearch] = useState("");
  const [divisionFilter, setDivisionFilter] = useState<"all" | "Male" | "Female">("all");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [weightFilter, setWeightFilter] = useState<string>("all");
  const [clubFilter, setClubFilter] = useState<string>("all");

  // Competition metadata — fetched once (cached); it rarely changes. The
  // registrations themselves come from the live listener below.
  const loadComp = async (force = false) => {
    if (!id) return;
    if (force) invalidateCachePrefix("competitions:");
    try {
      const comps = await getAllCompetitions();
      const found = (comps as any[]).find((c) => c.id === id) ?? null;
      setComp(found);
      setError("");
    } catch (err) {
      console.error("Failed to load competition:", err);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    loadComp(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Live registrations — a real-time Firestore listener so weight changes, new
  // registrations and admin removals appear here (e.g. on the championship
  // check-in laptop) within ~1 second, with no manual refresh and no stale
  // cache. The listener is scoped to one competition; its small result is
  // sorted client-side so this public view does not require a new composite index.
  useEffect(() => {
    if (!id) return;
    let active = true;
    const unsub = listenPublicRegistrations(
      id,
      (items) => {
        if (!active) return;
        setRegs(items);
        setLastLoaded(new Date());
        setError("");
        setLoading(false);
      },
      (err) => {
        console.error("Live registrations listener failed:", err);
        if (active) {
          setError(t("publicRegs.error") || "Failed to load registrations.");
          setLoading(false);
        }
      }
    );
    return () => { active = false; unsub(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadComp(true);
  };

  const filteredRegs = useMemo(() => {
    const q = search.trim().toLowerCase();
    return regs.filter((r) => {
      if (divisionFilter !== "all" && r.division !== divisionFilter) return false;
      if (categoryFilter !== "all" && (r.category ?? "—") !== categoryFilter) return false;
      if (weightFilter !== "all" && (r.weightCategory ?? "") !== weightFilter) return false;
      if (clubFilter !== "all") {
        const club = r.club && r.club.trim() ? r.club.trim() : NO_CLUB;
        if (club !== clubFilter) return false;
      }
      if (q) {
        const hay = `${r.athleteSnapshot?.firstName ?? ""} ${r.athleteSnapshot?.lastName ?? ""} ${r.club ?? ""} ${coachDisplay(r.coachName)}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [regs, search, divisionFilter, categoryFilter, weightFilter, clubFilter]);

  const grouped = useMemo(() => groupByCategory(filteredRegs), [filteredRegs]);

  const totalAthletes = filteredRegs.length;
  const totalClubs = useMemo(
    () => new Set(filteredRegs.map((r) => (r.club && r.club.trim() ? r.club.trim() : NO_CLUB))).size,
    [filteredRegs]
  );

  // Filter option lists. Categories and clubs come from the full set so a
  // dropdown never hides a value. Weights additionally respect the active
  // category + division so the list stays short and relevant.
  const categoryOptions = useMemo(() => {
    const set = new Set<string>();
    for (const r of regs) set.add(r.category ?? "—");
    return Array.from(set).sort((a, b) => categoryRank(a) - categoryRank(b));
  }, [regs]);

  const weightOptions = useMemo(() => {
    const set = new Set<string>();
    for (const r of regs) {
      if (divisionFilter !== "all" && r.division !== divisionFilter) continue;
      if (categoryFilter !== "all" && (r.category ?? "—") !== categoryFilter) continue;
      if (r.weightCategory) set.add(r.weightCategory);
    }
    return Array.from(set).sort((a, b) => weightRank(a) - weightRank(b));
  }, [regs, divisionFilter, categoryFilter]);

  const clubOptions = useMemo(() => {
    const set = new Set<string>();
    for (const r of regs) set.add(r.club && r.club.trim() ? r.club.trim() : NO_CLUB);
    return Array.from(set).sort((a, b) => {
      if (a === NO_CLUB) return 1;
      if (b === NO_CLUB) return -1;
      return a.localeCompare(b);
    });
  }, [regs]);

  // If the chosen weight is no longer offered (category/division changed),
  // fall back to "all" so the list doesn't silently go empty.
  useEffect(() => {
    if (weightFilter !== "all" && !weightOptions.includes(weightFilter)) {
      setWeightFilter("all");
    }
  }, [weightOptions, weightFilter]);

  // Weight-change deadline → once it passes, registrations are final.
  const weightDeadline = useMemo(() => (comp ? getWeightChangeDeadline(comp) : null), [comp]);
  const deadlinePassed = !!weightDeadline && weightDeadline.getTime() <= Date.now();
  const deadlineLabel = weightDeadline ? formatDate(weightDeadline, language) : "";
  // Whole calendar days from today until the deadline (0 == last day).
  const daysUntilDeadline = useMemo(() => {
    if (!weightDeadline) return null;
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    const startOfDeadline = new Date(weightDeadline);
    startOfDeadline.setHours(0, 0, 0, 0);
    return Math.round((startOfDeadline.getTime() - startOfToday.getTime()) / 86_400_000);
  }, [weightDeadline]);

  const clubLabel = (club: string) => (club === NO_CLUB ? t("publicRegs.noClub") : club);

  return (
    <>
      <Helmet>
        <title>
          {comp?.name
            ? `${comp.name} — ${t("publicRegs.title")}`
            : t("publicRegs.title")}
        </title>
        <meta name="description" content={t("publicRegs.metaDescription") || "Public list of athletes registered for this competition."} />
      </Helmet>

      <div style={s.page}>
        {/* Header */}
        <PublicHeader backTo="/events" backLabel={t("nav.backToEvents")} />

        <main style={{ ...s.main, padding: isMobile ? "20px 14px 48px" : "32px 24px 64px" }}>
          <div style={s.eyebrow}>{t("publicRegs.eyebrow")}</div>
          <h1 style={s.title}>{comp?.name || t("publicRegs.titleFallback")}</h1>

          <nav style={s.pageTabs} aria-label={t("publicDraws.navigation")}>
            <button style={{ ...s.pageTab, ...s.pageTabActive }}>
              {t("publicDraws.registrationsTab")}
            </button>
            <button style={s.pageTab} onClick={() => navigate(`/competition/${id}/draws`)}>
              {t("publicDraws.drawTab")}
            </button>
          </nav>

          {comp && (
            <div style={s.metaRow}>
              <span>
                {formatDate(comp.startDate, language)}
                {comp.endDate ? ` – ${formatDate(comp.endDate, language)}` : ""}
              </span>
              <span style={s.metaDot}>·</span>
              <span>
                {totalAthletes} {t("publicRegs.athletes")}
              </span>
              <span style={s.metaDot}>·</span>
              <span>
                {totalClubs} {t("publicRegs.clubs")}
              </span>
            </div>
          )}

          {/* Live status chips — derived from the competition's dates + flag so
              the federation can see at a glance (e.g. on the check-in laptop)
              exactly what is open/closed right now. */}
          {comp && (() => {
            const cs = getCompetitionStatus(comp);
            const chip = (label: string, tone: "green" | "red" | "gray" | "blue") => {
              const tones: Record<string, React.CSSProperties> = {
                green: { color: "var(--color-success)", background: "rgba(34,197,94,0.14)", border: "1px solid rgba(34,197,94,0.45)" },
                red:   { color: "var(--color-danger)", background: "rgba(239,68,68,0.12)", border: "1px solid rgba(239,68,68,0.45)" },
                gray:  { color: C.text2,   background: C.surface2,             border: `1px solid ${C.border}` },
                blue:  { color: C.blue, background: C.blueDim, border: `1px solid ${C.infoBorder}` },
              };
              return (
                <span
                  key={label}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 6,
                    padding: "6px 12px", borderRadius: 999, fontSize: 12,
                    fontWeight: 700, fontFamily: FONT.body, whiteSpace: "nowrap",
                    ...tones[tone],
                  }}
                >
                  {label}
                </span>
              );
            };
            return (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 16 }}>
                {cs.registrationOpen
                  ? chip(`🟢 ${t("publicRegs.status.regOpen")}`, "green")
                  : chip(`🔴 ${t("publicRegs.status.regClosed")}`, "red")}
                {cs.weightChangesOpen
                  ? chip(`⚖️ ${t("publicRegs.status.weightOpen")}`, "green")
                  : chip(`🔒 ${t("publicRegs.status.weightClosed")}`, "gray")}
                {cs.finished
                  ? chip(`🏁 ${t("publicRegs.status.finished")}`, "blue")
                  : cs.started
                  ? chip(`▶️ ${t("publicRegs.status.started")}`, "blue")
                  : null}
              </div>
            );
          })()}

          {/* Official document (provision / regulations) — public download. */}
          {comp?.documentUrl && (
            <a
              href={comp.documentUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                marginTop: 14,
                padding: "10px 16px",
                borderRadius: 8,
                border: `1px solid ${C.blue}`,
                backgroundColor: C.blueDim,
                color: C.blue,
                fontWeight: 700,
                fontSize: 13,
                textDecoration: "none",
                fontFamily: FONT.body,
              }}
            >
              📄 {t("publicRegs.officialDoc")}
            </a>
          )}

          {/* Second optional document (e.g. English version) — public download. */}
          {comp?.documentUrl2 && (
            <a
              href={comp.documentUrl2}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                marginTop: 14,
                marginLeft: comp?.documentUrl ? 8 : 0,
                padding: "10px 16px",
                borderRadius: 8,
                border: `1px solid ${C.blue}`,
                backgroundColor: C.blueDim,
                color: C.blue,
                fontWeight: 700,
                fontSize: 13,
                textDecoration: "none",
                fontFamily: FONT.body,
              }}
            >
              📄 {t("publicRegs.officialDoc2")}
            </a>
          )}

          {/* Weight-change deadline banner.
              • Before the cutoff: a highlighted "changes open until {date}" notice
                so coaches and the public can see the due date at a glance.
              • After the cutoff: the red "registrations final" lock banner. */}
          {weightDeadline && !deadlinePassed && (
            <div style={s.openBanner}>
              <ClockIcon size={17} color="#6ee7b7" style={{ marginTop: 1 }} />
              <span>
                <strong>
                  {t("publicRegs.openBanner").replace("{date}", deadlineLabel)}
                </strong>
                <span style={s.openBannerSub}>
                  {" "}
                  {daysUntilDeadline !== null && daysUntilDeadline <= 0
                    ? t("publicRegs.openBannerLastDay")
                    : daysUntilDeadline !== null
                    ? t("publicRegs.openBannerDays").replace("{days}", String(daysUntilDeadline))
                    : ""}
                </span>
              </span>
            </div>
          )}
          {deadlinePassed && (
            <div style={s.finalBanner}>
              <LockIcon size={17} color={C.danger} style={{ marginTop: 1 }} />
              <span>
                <strong>{t("publicRegs.finalBanner")}</strong>
                {deadlineLabel && (
                  <span style={s.finalBannerSub}>
                    {" "}
                    {t("publicRegs.finalNote").replace("{date}", deadlineLabel)}
                  </span>
                )}
              </span>
            </div>
          )}

          {/* Refresh row */}
          <div style={s.refreshRow}>
            <button
              style={refreshing ? s.refreshBtnDisabled : s.refreshBtn}
              onClick={handleRefresh}
              disabled={refreshing || loading}
            >
              <RefreshIcon size={14} style={refreshing ? { animation: "spin 0.7s linear infinite" } : undefined} />
              {refreshing ? t("publicRegs.refreshing") : t("publicRegs.refresh")}
            </button>
            {lastLoaded && (
              <span style={s.lastLoaded}>
                {t("publicRegs.lastUpdated")}{" "}
                {lastLoaded.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
              </span>
            )}
          </div>

          {/* Filters — search + division chips on row one, the category /
              weight / club drill-down dropdowns on row two. */}
          {!loading && regs.length > 0 && (
            <div style={s.filterBar}>
              <div style={s.filterRow}>
                <input
                  type="text"
                  placeholder={t("publicRegs.searchPlaceholder")}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={s.search}
                />
                <div style={s.chipGroup}>
                  {([
                    ["all", t("publicRegs.divisionAll") || "All"],
                    ["Male", t("publicRegs.divisionMale") || "Male"],
                    ["Female", t("publicRegs.divisionFemale") || "Female"],
                  ] as ["all" | "Male" | "Female", string][]).map(([key, label]) => (
                    <button
                      key={key}
                      style={{
                        ...s.chip,
                        ...(divisionFilter === key ? s.chipActive : {}),
                      }}
                      onClick={() => setDivisionFilter(key)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <div style={s.filterRow}>
                <select
                  style={s.filterSelect}
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                >
                  <option value="all">{t("publicRegs.allCategories")}</option>
                  {categoryOptions.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
                <select
                  style={s.filterSelect}
                  value={weightFilter}
                  onChange={(e) => setWeightFilter(e.target.value)}
                >
                  <option value="all">{t("publicRegs.allWeights")}</option>
                  {weightOptions.map((w) => (
                    <option key={w} value={w}>{w}</option>
                  ))}
                </select>
                <select
                  style={s.filterSelect}
                  value={clubFilter}
                  onChange={(e) => setClubFilter(e.target.value)}
                >
                  <option value="all">{t("publicRegs.allClubs")}</option>
                  {clubOptions.map((c) => (
                    <option key={c} value={c}>{clubLabel(c)}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {loading && <div style={s.stateBox}>{t("publicRegs.loading")}</div>}
          {error && <div style={s.errorBox}>{error}</div>}

          {!loading && !error && regs.length === 0 && (
            <div style={s.emptyBox}>
              <UsersIcon size={34} color="var(--color-text-subtle)" style={{ margin: "0 auto 12px" }} />
              <div>{t("publicRegs.empty")}</div>
            </div>
          )}

          {!loading && !error && regs.length > 0 && filteredRegs.length === 0 && (
            <div style={s.emptyBox}>{t("publicRegs.noMatch")}</div>
          )}

          {/* Grouped output — Category → weight bracket → athlete rows.
              Category is the dominant heading; athletes are listed flat
              (across clubs) inside each weight bracket. */}
          {!loading && !error && grouped.length > 0 && (
            <div style={s.catList}>
              {grouped.map((cg) => (
                <section key={cg.category} style={s.catSection}>
                  <div style={s.catHeader}>
                    <div style={s.catHeaderLeft}>
                      <span style={s.catAccent} />
                      <span style={s.catName}>{cg.category}</span>
                    </div>
                    <span style={s.catCount}>
                      <UsersIcon size={14} color="var(--color-text)" />
                      {cg.totalAthletes}
                    </span>
                  </div>

                  <div style={s.catBody}>
                    {cg.brackets.map((b) => (
                      <div key={b.key} style={s.bracket}>
                        <div style={s.bracketHeader}>
                          <span style={s.bracketWeight}>{b.weightCategory}</span>
                          <span
                            style={{
                              ...s.divBadge,
                              ...(b.division === "Female" ? s.divBadgeF : s.divBadgeM),
                            }}
                          >
                            {b.division === "Female"
                              ? t("publicRegs.divisionFemale") || "Female"
                              : t("publicRegs.divisionMale") || "Male"}
                          </span>
                          <span style={s.bracketCount}>{b.athletes.length}</span>
                        </div>

                        {/* Desktop column headers */}
                        {!isMobile && (
                          <div style={{ ...s.rowHeader, gridTemplateColumns: ROW_COLS }}>
                            <span>{t("publicRegs.colAthlete")}</span>
                            <span>{t("publicRegs.colClub")}</span>
                            <span>{t("publicRegs.coach")}</span>
                            <span>{t("publicRegs.colUpdated")}</span>
                          </div>
                        )}

                        {b.athletes.map((a, i) => {
                          const updated = a.weightLastChanged
                            ? t("publicRegs.weightUpdatedBy")
                                .replace("{by}", changedByDisplay(a.weightChangedBy) || "—")
                                .replace("{date}", formatDMY(a.weightLastChanged))
                            : "";
                          const isLast = i === b.athletes.length - 1;

                          return isMobile ? (
                            <div
                              key={i}
                              style={{
                                ...s.rowCard,
                                borderTop: i === 0 ? "none" : "1px solid var(--color-border)",
                              }}
                            >
                              <div style={s.rowCardName}>
                                {a.firstName} {a.lastName}
                              </div>
                              <div style={s.rowCardLine}>
                                <span style={s.rowCardLabel}>{t("publicRegs.colClub")}</span>
                                {clubLabel(a.club)}
                              </div>
                              <div style={s.rowCardLine}>
                                <span style={s.rowCardLabel}>{t("publicRegs.coach")}</span>
                                {a.coach}
                              </div>
                              {updated && (
                                <div style={s.rowCardLine}>
                                  <HistoryIcon size={11} color="var(--color-text-muted)" style={{ flexShrink: 0 }} />
                                  <span style={{ color: C.text2 }}>{updated}</span>
                                </div>
                              )}
                            </div>
                          ) : (
                            <div
                              key={i}
                              style={{
                                ...s.row,
                                gridTemplateColumns: ROW_COLS,
                                borderBottom: isLast ? "none" : "1px solid var(--color-border)",
                              }}
                            >
                              <span style={s.rowAthlete}>
                                {a.firstName} {a.lastName}
                              </span>
                              <span style={s.rowClub}>{clubLabel(a.club)}</span>
                              <span style={s.rowCoach}>{a.coach}</span>
                              {updated ? (
                                <span style={s.rowUpdated}>
                                  <HistoryIcon size={11} color="var(--color-text-muted)" style={{ flexShrink: 0 }} />
                                  <span
                                    style={{
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {updated}
                                  </span>
                                </span>
                              ) : (
                                <span style={s.rowUpdatedEmpty}>—</span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          )}
        </main>
      </div>
    </>
  );
}

const s: Record<string, React.CSSProperties> = {
  pageTabs: {
    display: "flex",
    gap: 4,
    marginTop: 22,
    marginBottom: 18,
    borderBottom: `1px solid ${C.border}`,
  },
  pageTab: {
    border: 0,
    borderBottom: "2px solid transparent",
    padding: "10px 15px",
    background: "transparent",
    color: C.text2,
    fontFamily: FONT.body,
    fontSize: 11,
    fontWeight: 800,
    cursor: "pointer",
  },
  pageTabActive: { borderBottom: `2px solid ${C.red}`, color: C.red },
  page: {
    minHeight: "100vh",
    backgroundColor: C.bg,
    color: C.text,
    fontFamily: FONT.body,
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 24px",
    borderBottom: "1px solid var(--color-border)",
    backgroundColor: "rgba(7,10,15,0.9)",
    position: "sticky",
    top: 0,
    zIndex: 10,
    backdropFilter: "blur(8px)",
  },
  backBtn: {
    background: "none",
    border: `1px solid ${C.border2}`,
    borderRadius: 8,
    color: C.text2,
    padding: "6px 12px",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: FONT.body,
  },
  brand: { display: "flex", alignItems: "center", gap: 10 },
  brandLogo: { width: 32, height: 32, objectFit: "contain" },
  brandTitle: {
    fontFamily: FONT.display,
    fontSize: 14,
    letterSpacing: "3px",
    color: C.text,
    lineHeight: 1.1,
  },
  brandSub: {
    fontFamily: FONT.display,
    fontSize: 9,
    letterSpacing: "2.5px",
    color: C.text3,
    lineHeight: 1,
  },
  main: { maxWidth: 1200, margin: "0 auto", padding: "32px 24px 64px" },
  eyebrow: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: "2px",
    color: C.red,
    textTransform: "uppercase",
    marginBottom: 6,
  },
  title: {
    fontFamily: FONT.display,
    fontSize: "clamp(28px, 5vw, 44px)",
    letterSpacing: "1px",
    color: C.text,
    margin: "0 0 12px",
    lineHeight: 1.1,
  },
  metaRow: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 8,
    fontSize: 13,
    color: C.text2,
    marginBottom: 24,
  },
  metaDot: { color: C.text3 },
  finalBanner: {
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
    backgroundColor: "rgba(218,41,28,0.12)",
    border: "1px solid rgba(218,41,28,0.4)",
    borderRadius: 10,
    padding: "12px 16px",
    marginBottom: 20,
    fontSize: 13,
    color: C.danger,
    lineHeight: 1.5,
  },
  finalBannerSub: { color: C.text2, fontWeight: 400 },
  openBanner: {
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
    backgroundColor: "rgba(16,185,129,0.12)",
    border: "1px solid rgba(16,185,129,0.45)",
    borderRadius: 10,
    padding: "12px 16px",
    marginBottom: 20,
    fontSize: 13,
    color: C.success,
    lineHeight: 1.5,
  },
  openBannerSub: { color: C.text2, fontWeight: 400 },
  refreshRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginBottom: 20,
    flexWrap: "wrap",
  },
  refreshBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    backgroundColor: "rgba(218,41,28,0.85)",
    color: C.onAccent,
    border: "none",
    borderRadius: 8,
    padding: "8px 16px",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
    letterSpacing: "0.3px",
    fontFamily: FONT.body,
  },
  refreshBtnDisabled: {
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    backgroundColor: C.surface2,
    color: C.text2,
    border: "none",
    borderRadius: 8,
    padding: "8px 16px",
    fontSize: 12,
    fontWeight: 700,
    cursor: "not-allowed",
    fontFamily: FONT.body,
  },
  lastLoaded: { fontSize: 11, color: C.text2 },
  filterBar: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    marginBottom: 20,
    padding: "10px 12px",
    backgroundColor: C.surface2,
    border: `1px solid ${C.border}`,
    borderRadius: 10,
  },
  filterRow: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
    alignItems: "center",
  },
  search: {
    flex: "1 1 240px",
    minWidth: 0,
    padding: "9px 12px",
    border: `1px solid ${C.border2}`,
    borderRadius: 7,
    fontSize: 13,
    backgroundColor: C.surface,
    color: C.text,
    outline: "none",
    fontFamily: FONT.body,
  },
  chipGroup: { display: "flex", gap: 6, flexWrap: "wrap" },
  chip: {
    backgroundColor: C.surface2,
    color: C.text,
    border: `1px solid ${C.border2}`,
    borderRadius: 20,
    padding: "6px 12px",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: FONT.body,
  },
  chipActive: {
    backgroundColor: C.red,
    color: C.onAccent,
    borderColor: C.red,
  },
  stateBox: {
    textAlign: "center",
    padding: "60px 20px",
    color: C.text2,
    fontSize: 14,
  },
  errorBox: {
    backgroundColor: "rgba(218,41,28,0.12)",
    border: "1px solid rgba(218,41,28,0.4)",
    color: C.danger,
    borderRadius: 8,
    padding: "12px 16px",
    fontSize: 13,
  },
  emptyBox: {
    textAlign: "center",
    padding: "60px 20px",
    backgroundColor: C.surface,
    border: `1px dashed ${C.border2}`,
    borderRadius: 10,
    color: C.text2,
    fontSize: 14,
  },

  // ── Filter dropdowns ────────────────────────────────────────────────────────
  filterSelect: {
    padding: "8px 10px",
    border: `1px solid ${C.border2}`,
    borderRadius: 7,
    fontSize: 13,
    backgroundColor: C.surface,
    color: C.text,
    outline: "none",
    cursor: "pointer",
    fontFamily: FONT.body,
    flex: "1 1 150px",
    minWidth: 0,
  },

  // ── Category → weight-bracket grouping ──────────────────────────────────────
  catList: { display: "flex", flexDirection: "column", gap: 22 },
  catSection: {
    backgroundColor: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 12,
    overflow: "hidden",
  },
  catHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "15px 20px",
    backgroundColor: "rgba(218,41,28,0.12)",
    borderBottom: "1px solid rgba(218,41,28,0.25)",
  },
  catHeaderLeft: { display: "flex", alignItems: "center", gap: 12, minWidth: 0 },
  catAccent: { width: 5, height: 28, borderRadius: 3, backgroundColor: C.red, flexShrink: 0 },
  catName: {
    fontFamily: FONT.display,
    fontSize: "clamp(22px, 3vw, 30px)",
    letterSpacing: "1.5px",
    color: C.text,
    textTransform: "uppercase",
    lineHeight: 1.05,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  catCount: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    fontSize: 13,
    color: C.text,
    fontWeight: 700,
    flexShrink: 0,
  },
  catBody: { padding: "12px 14px 16px", display: "flex", flexDirection: "column", gap: 12 },

  bracket: {
    backgroundColor: C.surface2,
    borderRadius: 10,
    border: "1px solid var(--color-border)",
    overflow: "hidden",
  },
  bracketHeader: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 14px",
    borderBottom: "1px solid var(--color-border)",
    backgroundColor: "var(--color-surface-2)",
  },
  bracketWeight: {
    fontSize: 16,
    fontWeight: 800,
    color: C.text,
    fontFamily: FONT.body,
    letterSpacing: "0.3px",
  },
  bracketCount: {
    fontSize: 11,
    fontWeight: 700,
    color: C.text2,
    backgroundColor: C.surface2,
    padding: "2px 9px",
    borderRadius: 10,
    marginLeft: "auto",
  },
  divBadge: {
    fontSize: 9.5,
    fontWeight: 800,
    letterSpacing: "0.5px",
    borderRadius: 4,
    padding: "2px 7px",
    textTransform: "uppercase",
    flexShrink: 0,
  },
  divBadgeM: {
    backgroundColor: "rgba(59,130,246,0.16)",
    color: "#93c5fd",
    border: "1px solid rgba(59,130,246,0.35)",
  },
  divBadgeF: {
    backgroundColor: "rgba(236,72,153,0.16)",
    color: "#f9a8d4",
    border: "1px solid rgba(236,72,153,0.35)",
  },

  rowHeader: {
    display: "grid",
    gap: 12,
    padding: "7px 14px",
    fontSize: 9.5,
    fontWeight: 700,
    letterSpacing: "0.8px",
    textTransform: "uppercase",
    color: C.text3,
    borderBottom: `1px solid ${C.border}`,
  },
  row: {
    display: "grid",
    gap: 12,
    padding: "10px 14px",
    alignItems: "center",
  },
  rowAthlete: {
    fontSize: 14,
    fontWeight: 600,
    color: C.text,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  rowClub: {
    fontSize: 12.5,
    color: "var(--color-text)",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  rowCoach: {
    fontSize: 12.5,
    color: "var(--color-text)",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  rowUpdated: {
    fontSize: 11.5,
    color: C.text2,
    display: "flex",
    alignItems: "center",
    gap: 5,
    minWidth: 0,
  },
  rowUpdatedEmpty: { fontSize: 12, color: C.text3 },

  rowCard: {
    padding: "11px 14px",
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  rowCardName: { fontSize: 14.5, fontWeight: 700, color: C.text, marginBottom: 2 },
  rowCardLine: {
    fontSize: 12.5,
    color: "var(--color-text)",
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  rowCardLabel: {
    fontSize: 9,
    fontWeight: 700,
    letterSpacing: "0.5px",
    textTransform: "uppercase",
    color: C.text2,
    flexShrink: 0,
  },
};
